import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import Chart from './Chart';
import ControlsView from './components/ControlsView';
import { useMenuContext } from './context';
import ControlViewSwitch from './components/ControlViewSwitch';
import CountryIncomeLegend from './components/CountryIncomeLegend';
import ResilienceIndexLegend from './components/ResilienceIndexLegend';
import ScaleLegend from './components/ScaleLegend';
import ChartButtons from './components/ChartButtons';
import YearToggle from './components/YearToggle';
import CountryTooltip from './components/CountryTooltip';
import ViewNotices from './components/ViewNotices';
import { useUrlState } from './useUrlState';

const DRAWER_WIDTH = 360 + 20 * 2;

// Everything that used to float over the chart with negative top offsets -- the
// year toggle, the ?/download buttons, the panel switch -- now sits in one row in
// normal flow. In flow it can wrap, which is what a tablet needs; floated it
// collided with itself at 768px.
const TopBar = styled('header')(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '8px 16px',
	// The panel switch carries its own 20px padding, which is what sets the header
	// to its original 99px; the other items centre within that.
	padding: '0 20px',
	minHeight: theme.menu.size,
	boxSizing: 'border-box',
	[theme.breakpoints.down('md')]: {
		minHeight: 0,
		padding: '10px 12px',
	},
}));

const ChartWrapper = styled('main', {
	shouldForwardProp: (prop) => prop !== 'open' && prop !== 'narrow',
})(({ theme, open, narrow }) => ({
	flexGrow: 1,
	minWidth: 0,
	position: 'relative',
	padding: 20,
	transition: theme.transitions.create('margin', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.shortest,
	}),
	// On a wide screen the panel docks beside the chart and pushes it over. On a
	// narrow one it overlays instead, so the chart keeps the full width.
	marginRight: narrow ? 0 : -DRAWER_WIDTH,
	...(open &&
		!narrow && {
			transition: theme.transitions.create('margin', {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.shorter,
			}),
			marginRight: 0,
		}),
	[theme.breakpoints.down('md')]: {
		padding: 12,
	},
}));

export default function App() {
	const { isMenuOpen, setMenuOpen } = useMenuContext();
	const theme = useTheme();
	const narrow = useMediaQuery(theme.breakpoints.down('md'));

	useUrlState();

	// The layout decides the panel's resting state: docked open when there is room
	// for it, closed when it would be an overlay covering the whole chart. This runs
	// on mount and whenever the viewport crosses the breakpoint, so a tablet turned
	// to landscape gets the dock and turned back loses it.
	React.useEffect(() => {
		setMenuOpen(!narrow);
	}, [narrow, setMenuOpen]);

	return (
		<Box height={'100vh'} display={'flex'} flexDirection={'column'} overflow={'hidden'}>
			<TopBar>
				<YearToggle />
				<Box flex={1} minWidth={0} />
				<ChartButtons />
				{narrow ? (
					<Tooltip title="Filters and settings" placement="bottom" arrow>
						<IconButton
							color="primary"
							size={'large'}
							onClick={() => setMenuOpen(true)}
							aria-label="Open filters and settings"
						>
							<span className="material-symbols-outlined" style={{ fontSize: 24 }}>
								tune
							</span>
						</IconButton>
					</Tooltip>
				) : (
					<ControlViewSwitch />
				)}
			</TopBar>

			<Box display={'flex'} flex={1} minHeight={0} position={'relative'}>
				<ChartWrapper open={isMenuOpen} narrow={narrow}>
					<Chart />
					<CountryIncomeLegend />
					<ResilienceIndexLegend />
					<ScaleLegend />
					<ViewNotices />
				</ChartWrapper>
				<CountryTooltip />
				<Drawer
					variant={narrow ? 'temporary' : 'persistent'}
					anchor="right"
					open={isMenuOpen}
					onClose={() => setMenuOpen(false)}
					ModalProps={{ keepMounted: true }}
					sx={{
						width: narrow ? 0 : DRAWER_WIDTH,
						flexShrink: 0,
						'& .MuiDrawer-paper': {
							width: narrow ? 'min(100vw, 420px)' : DRAWER_WIDTH,
							boxSizing: 'border-box',
							border: 'none',
							...(!narrow && { position: 'absolute', top: 0, height: '100%' }),
						},
					}}
				>
					{narrow && <ControlViewSwitch />}
					<ControlsView />
				</Drawer>
			</Box>
		</Box>
	);
}
